import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Home, FlaskConical, Swords, Zap, Boxes, Building2, X, ChevronsUp, Loader2, Users, HardHat, Route, Skull, Radar } from "lucide-react";
import { spaceApi } from "./spaceApi";
import { sfx } from "./audio";
import WorldMap from "./WorldMap";
import SkinStore from "./SkinStore";
import SquadRoster from "./SquadRoster";
import SquadDeploy from "./SquadDeploy";
import PvpArena from "./PvpArena";
import BaseStaff from "./BaseStaff";
import RoadShooter from "./RoadShooter";
import StoryCampaign from "./StoryCampaign";
import BunkerHub from "./BunkerHub";
import SurfaceCompound from "./SurfaceCompound";
import { SpaceHomeBtn, ResetGameControl, SpaceMuteBtn } from "./SpaceNav";

const BGM_URL = "/assets/vault/audio/command_deck_theme.mp3";

const PVP_UNLOCK_LEVEL = 20;
const PHASE_LABEL = {
  PLANET_SURVIVAL: "PLANET_SURVIVAL", PLANET_PVP_ARENA: "PLANET_PVP_ARENA",
  ORBITAL_CHALLENGER: "ORBITAL_CHALLENGER", SPACE_CITY_CHAMPION: "SPACE_CITY_CHAMPION",
};
const PRESETS = {
  LAB: { key: "LAB", name: "Resource Extraction Lab", baseYield: 5, initialCost: 100, Icon: FlaskConical },
  ARMOURY: { key: "ARMOURY", name: "Tactical Armoury Ward", baseYield: 2, initialCost: 150, Icon: Swords },
};
const upgradeCost = (initial, level) => Math.round(initial * Math.pow(1.6, level));
const ccUpgradeCost = (level) => 200 * level;

// Sanctuary Base — Phase 3 grid, now SERVER-AUTHORITATIVE (Phase: Server Save).
// Titanium accrues + all build/upgrade costs validate on the backend; the client
// only issues intents. A local display clock keeps the counter smooth between syncs.
export default function BaseMapShell({ onHome }) {
  const [srv, setSrv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [drawerPlot, setDrawerPlot] = useState(null);
  const [view, setView] = useState("base");
  const srvRef = useRef(null);
  useEffect(() => { srvRef.current = srv; }, [srv]);

  useEffect(() => {
    spaceApi.getState().then((s) => setSrv(s)).catch(() => toast.error("Could not load your base")).finally(() => setLoading(false));
  }, []);
  // Command-deck ambient BGM (idempotent; resumes on first tap if the browser blocked autoplay).
  useEffect(() => { sfx.startBgm(BGM_URL); }, []);
  // Optimistic per-second display clock + periodic authoritative reconcile.
  useEffect(() => {
    const tick = setInterval(() => setSrv((s) => (s && s.total_yield ? { ...s, titanium: s.titanium + s.total_yield } : s)), 1000);
    const rec = setInterval(() => spaceApi.getState().then(setSrv).catch(() => {}), 20000);
    return () => { clearInterval(tick); clearInterval(rec); };
  }, []);

  const mapErr = (e) => e.status === 402 ? "Not enough Titanium" : e.status === 409 ? "Blocked — cap reached or plot occupied" : (e.message || "Action failed");
  const doAction = useCallback(async (fn) => {
    if (busy) return null;
    setBusy(true);
    try { const s = await fn(); setSrv(s); return s; }
    catch (e) { toast.error(mapErr(e)); return null; }
    finally { setBusy(false); }
  }, [busy]);

  const build = async (plotId, key) => {
    const s = await doAction(() => spaceApi.build(plotId, key));
    if (s) toast.success(`${PRESETS[key].name} deployed`);
    setDrawerPlot(null);
  };
  const upgradeStructure = (plotId) => doAction(() => spaceApi.upgradeStructure(plotId));
  const resetGame = async () => {
    try {
      const s = await spaceApi.adminReset();
      setSrv(s); setView("base");
      toast.success("Colony reset to baseline startup");
    } catch { toast.error("Reset failed"); }
  };  const upgradeCC = async () => {
    const before = srvRef.current?.commandCentreLevel || 1;
    const s = await doAction(() => spaceApi.upgradeCC());
    if (s) {
      if (s.phase === "PLANET_PVP_ARENA" && before < PVP_UNLOCK_LEVEL) toast.success("Command Centre Lv 20 — PvP Arena unlocked!");
      else toast.success(`Command Centre → Lv ${s.commandCentreLevel}`);
    }
  };

  if (loading || !srv) {
    return (
      <div className="ss-viewport" data-testid="space-supremacy-app">
        <div className="ss-atmosphere" />
        <button className="ss-home-btn" data-testid="space-home-btn" onClick={onHome}><Home size={18} /></button>
        <SpaceMuteBtn testid="space-mute-btn" />
        <div className="bg-loading" data-testid="space-base-loading"><Loader2 size={28} className="bg-spin" /><span>Syncing colony…</span></div>
      </div>
    );
  }

  if (view === "world") return <WorldMap onBack={() => { setView("base"); spaceApi.getState().then(setSrv).catch(() => {}); }} onDeploy={() => setView("deploy")} />;
  if (view === "skins") return <SkinStore onBack={() => { setView("base"); spaceApi.getState().then(setSrv).catch(() => {}); }} />;
  if (view === "roster") return <SquadRoster onBack={() => setView("base")} />;
  if (view === "deploy") return <SquadDeploy onBack={() => { setView("base"); spaceApi.getState().then(setSrv).catch(() => {}); }} />;
  if (view === "arena") return <PvpArena onBack={() => { setView("base"); spaceApi.getState().then(setSrv).catch(() => {}); }} />;
  if (view === "staff") return <BaseStaff onBack={() => setView("base")} />;
  if (view === "roadpatrol") return <RoadShooter onBack={() => setView("base")} />;
  if (view === "story") return <StoryCampaign onBack={() => setView("bunkerhub")} />;
  if (view === "bunkerhub") return <BunkerHub onBack={() => setView("base")} onDeploy={() => setView("story")} />;
  if (view === "surface") return <SurfaceCompound onBack={() => setView("base")} />;

  const ccLevel = srv.commandCentreLevel;
  const ccCost = ccUpgradeCost(ccLevel);

  return (
    <div className="ss-viewport" data-testid="space-supremacy-app">
      <div className="ss-atmosphere" />
      <div className="ss-grid" />
      <button className="ss-home-btn" data-testid="space-home-btn" onClick={onHome} aria-label="Back to Games Hub"><Home size={18} /></button>
      <SpaceMuteBtn testid="space-mute-btn" />

      <div className="bg-wrap" data-testid="space-base-shell">
        <div className="bg-hud">
          <div className="bg-hud-row">
            <span className="bg-status">[ CLOUD SAVE: ONLINE ]</span>
            <span className="bg-tier" data-testid="base-hud-tier">TIER: {PHASE_LABEL[srv.phase]}</span>
          </div>
          <div className="bg-hud-row">
            <span className="bg-res"><Boxes size={12} /> TITANIUM: <b data-testid="base-hud-titanium">{Math.floor(srv.titanium)}</b></span>
            <span className="bg-res alloy"><HardHat size={12} /> ALLOY: <b data-testid="base-hud-alloy">{Math.floor(srv.micro_alloy || 0)}</b></span>
            <span className="bg-yield"><Zap size={12} /> +<b data-testid="base-hud-yield">{srv.total_yield}</b>/s</span>
          </div>
        </div>

        <div className="bg-cc" data-testid="base-cc">
          <div className="bg-cc-left">
            <Building2 size={18} />
            <div>
              <span className="bg-cc-name">Command Centre</span>
              <span className="bg-cc-lv">Lv <b data-testid="base-cc-level">{ccLevel}</b> / {PVP_UNLOCK_LEVEL} · caps all structures</span>
            </div>
          </div>
          <button className="bg-btn cc" onClick={upgradeCC} disabled={busy || srv.titanium < ccCost} data-testid="base-cc-upgrade">
            <ChevronsUp size={13} /> UP [{ccCost} T]
          </button>
        </div>

        <div className="bg-section">Command Sector Grid</div>

        <button className="bg-conquest live" onClick={() => setView("roster")} data-testid="base-open-roster" style={{ borderColor: "rgba(0,213,255,0.6)", background: "rgba(0,213,255,0.12)", color: "#bdeeff" }}>
          <Users size={15} /> Squad Roster
        </button>

        <button className="bg-conquest live" onClick={() => setView("deploy")} data-testid="base-open-deploy" style={{ borderColor: "rgba(57,255,122,0.6)", background: "rgba(57,255,122,0.12)", color: "#c6ffdd", boxShadow: "0 0 18px rgba(57,255,122,0.2)" }}>
          <Users size={15} /> Squad Deployment · Sentry Duty
        </button>

        <button className="bg-conquest live" onClick={() => setView("arena")} data-testid="base-open-arena" style={{ borderColor: "rgba(255,59,78,0.6)", background: "rgba(255,59,78,0.12)", color: "#ffc9cf", boxShadow: "0 0 18px rgba(255,59,78,0.2)" }}>
          <Users size={15} /> PvP Challenge Arena
        </button>

        <button className="bg-conquest live" onClick={() => setView("staff")} data-testid="base-open-staff" style={{ borderColor: "rgba(245,158,11,0.55)", background: "rgba(245,158,11,0.12)", color: "#fcd9a0" }}>
          <HardHat size={15} /> Base Staff
        </button>

        <button className="bg-conquest live" onClick={() => setView("roadpatrol")} data-testid="base-open-roadpatrol" style={{ borderColor: "rgba(0,213,255,0.6)", background: "rgba(0,213,255,0.12)", color: "#bdeeff" }}>
          <Route size={15} /> Road Patrol
        </button>

        <button className="bg-conquest live" onClick={() => setView("bunkerhub")} data-testid="base-open-bunkerhub" style={{ borderColor: "rgba(79,195,255,0.7)", background: "rgba(79,195,255,0.14)", color: "#bfe6ff", boxShadow: "0 0 18px rgba(79,195,255,0.22)" }}>
          <Building2 size={15} /> Bunker Hub · Staging
        </button>

        <button className="bg-conquest live" onClick={() => setView("surface")} data-testid="base-open-surface" style={{ borderColor: "rgba(230,140,70,0.7)", background: "rgba(230,140,70,0.14)", color: "#ffd8b0", boxShadow: "0 0 18px rgba(230,140,70,0.22)" }}>
          <Radar size={15} /> Surface Compound · Sectors 61-150
        </button>

        <button className="bg-conquest live" onClick={() => setView("story")} data-testid="base-open-story" style={{ borderColor: "rgba(245,120,60,0.7)", background: "rgba(245,120,60,0.14)", color: "#ffd0b0", boxShadow: "0 0 18px rgba(245,120,60,0.22)" }}>
          <Skull size={15} /> Story · Crash Landing
        </button>

        <button className={`bg-conquest ${srv.phase === "PLANET_PVP_ARENA" ? "live" : "locked"}`}
          onClick={() => srv.phase === "PLANET_PVP_ARENA" && setView("world")}
          disabled={srv.phase !== "PLANET_PVP_ARENA"} data-testid="base-open-conquest">
          <Swords size={15} />
          {srv.phase === "PLANET_PVP_ARENA" ? "Open Conquest Matrix" : `Conquest unlocks at CC Lv ${PVP_UNLOCK_LEVEL}`}
        </button>

        <button className="bg-conquest live" onClick={() => setView("skins")} data-testid="base-open-skins" style={{ borderColor: "rgba(139,92,246,0.6)", background: "rgba(139,92,246,0.14)", color: "#c4b5fd", boxShadow: "0 0 18px rgba(139,92,246,0.2)" }}>
          <FlaskConical size={15} /> Skin Store
        </button>
        <div className="bg-grid">
          {srv.plots.map((plot) => {
            if (plot.type === "EMPTY") {
              return (
                <div className="bg-plot" data-testid={`base-plot-${plot.id}`} key={plot.id}>
                  <div>
                    <div className="bg-plot-type empty">[ EMPTY PLOT ]</div>
                    <div className="bg-plot-meta">Sector node available</div>
                  </div>
                  <button className="bg-btn" onClick={() => setDrawerPlot(plot.id)} data-testid={`base-plot-establish-${plot.id}`}>Establish</button>
                </div>
              );
            }
            const preset = PRESETS[plot.type];
            const Icon = preset.Icon;
            const cost = upgradeCost(preset.initialCost, plot.level);
            const capped = plot.level >= ccLevel;
            return (
              <div className="bg-plot active" data-testid={`base-plot-${plot.id}`} key={plot.id}>
                <div>
                  <div className="bg-plot-type"><Icon size={13} /> {plot.type}</div>
                  <div className="bg-plot-meta">LVL {plot.level}</div>
                  <div className="bg-plot-yield">+{plot.yield}/sec</div>
                </div>
                <button className="bg-btn" onClick={() => upgradeStructure(plot.id)} disabled={busy || srv.titanium < cost || capped} data-testid={`base-plot-upgrade-${plot.id}`}>
                  {capped ? "CC CAP" : `UP [${cost} T]`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="ss-sysfooter" data-testid="space-system-footer">
          <span className="ss-sysfooter-l">System Management</span>
          <ResetGameControl onReset={resetGame} />
        </div>
      </div>

      {drawerPlot !== null && (
        <div className="bg-drawer" data-testid="base-build-drawer">
          <div className="bg-drawer-head">
            <span>SELECT STRUCTURAL VARIANT</span>
            <button onClick={() => setDrawerPlot(null)} data-testid="base-drawer-close"><X size={15} /></button>
          </div>
          {Object.values(PRESETS).map((p) => {
            const Icon = p.Icon;
            const afford = srv.titanium >= p.initialCost;
            return (
              <div className="bg-opt" key={p.key}>
                <div>
                  <div className="bg-opt-name"><Icon size={13} /> {p.key}</div>
                  <div className="bg-opt-meta">Yield +{p.baseYield}/s · Cost {p.initialCost} T</div>
                </div>
                <button className="bg-btn" disabled={busy || !afford} onClick={() => build(drawerPlot, p.key)} data-testid={`base-build-${p.key}`}>Deploy</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
